import moment from 'moment';
import {
  CHANNEL_TILES,
  getEmailDelivered,
  getSent,
  getTileRates,
  isCounted,
} from './rates';

// One point per message, oldest first: { sendTime, openRate }.
// Messages with no email deliveries are left out.
export const getOpenRatePoints = messages =>
  messages
    .filter(message => getEmailDelivered(message) > 0)
    .map(message => ({
      sendTime: message.sendTime,
      openRate: getTileRates(message).open,
    }))
    .sort((a, b) => a.sendTime - b.sendTime);

// ---------------------------------------------------------------------------
// Detail page graph: one point per day for one message.

// Fixed colour per metric. Open and Click-through are the design's colours;
// the other four are from the dataviz palette, checked as a set of six with
// its validator (all checks pass; the CVD and contrast warnings are covered by
// the legend and the "Showing stats for" line, which name every line).
export const METRIC_COLORS = {
  delivery: '#1baf7a',
  open: '#1A3EC8',
  clickThrough: '#D6307F',
  bounce: '#eda100',
  drop: '#008300',
  spam: '#9085e9',
};

const fraction = (numerator, denominator) =>
  denominator ? numerator / denominator : 0;

// One version's value for a metric on one day, as a fraction of the
// message's total denominator, so the days add up to the tile's main value.
// - total: { email, text, resend } from the list endpoint's stats
// - day: { email, text, resend } from the daily endpoint
// Under 'all', Delivery and Bounce add email and text counts; the resend's
// opens and clicks are added to Open and Click-through, as on the tiles.
const getVersionDayValue = (metric, channel, total, day) => {
  const useEmail = channel !== 'text';
  const useText = channel !== 'email';
  const sent =
    (useEmail ? total.email.sent : 0) + (useText ? total.text.sent : 0);

  switch (metric) {
    case 'delivery':
      return fraction(
        (useEmail ? day.email.delivered : 0) +
          (useText ? day.text.delivered : 0),
        sent
      );
    case 'bounce':
      return fraction(
        (useEmail ? day.email.bounce : 0) + (useText ? day.text.bounced : 0),
        sent
      );
    case 'open':
      return fraction(
        day.email.uniqueOpen + (day.resend?.uniqueOpen || 0),
        total.email.delivered
      );
    case 'clickThrough':
      return fraction(
        day.email.uniqueClick + (day.resend?.uniqueClick || 0),
        total.email.uniqueOpen + (total.resend?.uniqueOpen || 0)
      );
    case 'drop':
      return fraction(day.email.dropped, total.email.sent);
    default:
      return fraction(day.email.spamReport, total.email.sent);
  }
};

const EMPTY_DAY = {
  email: {
    delivered: 0,
    uniqueOpen: 0,
    uniqueClick: 0,
    bounce: 0,
    dropped: 0,
    spamReport: 0,
  },
  text: { delivered: 0, bounced: 0 },
};

// Points for the detail graph: [{ date, [metric]: percent }], one per day
// from `start` (unix seconds, local midnight) for `dayCount` days, with zeros
// for days the daily endpoint left out. `date` is the day's local midnight.
// An A/B message's value is the mean of its A and B values.
export const getDailySeries = ({
  days,
  message,
  channel,
  metrics,
  start,
  dayCount,
}) => {
  const { stats } = message;
  const byDay = new Map(days.map(day => [day.day, day]));
  const totalA = {
    email: stats.email,
    text: stats.text,
    resend: stats.smartResend?.email,
  };
  const totalB = stats.versionB && {
    email: stats.versionB.email,
    text: stats.versionB.text,
    resend: stats.smartResend?.versionB?.email,
  };

  return Array.from({ length: dayCount }, (_, index) => {
    const entry = byDay.get(index) || EMPTY_DAY;
    const dayA = {
      email: entry.email,
      text: entry.text,
      resend: entry.smartResend?.email,
    };
    const point = {
      date: moment.unix(start).add(index, 'days').unix(),
    };
    for (const metric of metrics) {
      const valueA = getVersionDayValue(metric, channel, totalA, dayA);
      if (!totalB) {
        point[metric] = valueA * 100;
        continue;
      }
      const dayB = entry.versionB
        ? {
            email: entry.versionB.email,
            text: entry.versionB.text,
            resend: entry.smartResend?.versionB?.email,
          }
        : EMPTY_DAY;
      const valueB = getVersionDayValue(metric, channel, totalB, dayB);
      point[metric] = ((valueA + valueB) / 2) * 100;
    }
    return point;
  });
};

// The daily endpoint's range for a date range [startMoment, endMoment]:
// the first day's local midnight and the number of days, both ends included.
export const getDayRange = ([first, last]) => {
  const start = first.clone().startOf('day');
  return {
    start: start.unix(),
    days: last.clone().startOf('day').diff(start, 'days') + 1,
  };
};

// The longest graph range, in days, on the message page and on the combined
// stats page (10 years). The daily endpoints have the same limits.
export const MAX_RANGE_DAYS = 366;
export const MAX_COMBINED_RANGE_DAYS = 3653;

// Whether `date` is blocked in an open range picker because it would make the
// range longer than maxDays. calendarDates: the [first, last] dates clicked
// while the picker is open (antd's onCalendarChange), or null.
export const isOutsideMaxRange = (date, calendarDates, maxDays) => {
  if (!calendarDates) return false;
  const [first, last] = calendarDates;
  return (
    (!!first && date.diff(first, 'days') >= maxDays) ||
    (!!last && last.diff(date, 'days') >= maxDays)
  );
};

// The default graph range: the send date through 29 days later.
export const DEFAULT_RANGE_DAYS = 30;
export const getDefaultRange = sendTime => {
  const first = moment.unix(sendTime).startOf('day');
  return [first, first.clone().add(DEFAULT_RANGE_DAYS - 1, 'days')];
};

// The metrics to keep on the graph when the channel changes: those that exist
// under the new channel, in tile order; when none are left, Delivery.
export const getMetricsForChannel = (metrics, channel) => {
  const kept = CHANNEL_TILES[channel].filter(key => metrics.includes(key));
  return kept.length > 0 ? kept : ['delivery'];
};

// ---------------------------------------------------------------------------
// Combined stats page graph: the selected messages' daily values, averaged.

// Points for the combined graph, in the shape getDailySeries returns. For each
// day and metric: the sum of each counted message's value for that day
// (getDailySeries), divided by the number of messages counted for that metric
// (isCounted), as on the tiles. 0 when no message is counted.
// daysById: { [messageId]: days } from the multi-message daily endpoint.
export const getCombinedDailySeries = ({
  messages,
  daysById,
  channel,
  metrics,
  start,
  dayCount,
}) => {
  const seriesById = new Map(
    messages.map(message => [
      message.id,
      getDailySeries({
        days: daysById[message.id] || [],
        message,
        channel,
        metrics,
        start,
        dayCount,
      }),
    ])
  );
  const countedByMetric = Object.fromEntries(
    metrics.map(metric => [
      metric,
      messages.filter(message => isCounted(message, metric, channel)),
    ])
  );

  return Array.from({ length: dayCount }, (_, index) => {
    const point = { date: moment.unix(start).add(index, 'days').unix() };
    for (const metric of metrics) {
      const counted = countedByMetric[metric];
      point[metric] =
        counted.length === 0
          ? 0
          : counted.reduce(
              (sum, message) => sum + seriesById.get(message.id)[index][metric],
              0
            ) / counted.length;
    }
    return point;
  });
};

// The local midnight (unix seconds) of the earliest send date among the
// messages that sent something, or null when none did.
export const getCombinedStart = messages => {
  const sendTimes = messages
    .filter(message => {
      const sent = getSent(message);
      return sent.email + sent.text > 0;
    })
    .map(message => message.sendTime);
  if (sendTimes.length === 0) return null;
  return moment
    .unix(Math.min(...sendTimes))
    .startOf('day')
    .unix();
};

// The default combined range: from `start` to the last day with an event in
// any message, as [startMoment, endMoment]. daysById comes from a request of
// MAX_COMBINED_RANGE_DAYS days from `start`, so the range is at most that long. With no
// events, the range is the first day only.
export const getDefaultCombinedRange = (start, daysById) => {
  const lastDay = Math.max(
    0,
    ...Object.values(daysById).flatMap(days => days.map(entry => entry.day))
  );
  const first = moment.unix(start);
  return [first, first.clone().add(lastDay, 'days')];
};

// The resend marker text for one resend.
export const getResendLabel = resendTime =>
  `Resend to non-openers ${
    resendTime > moment().unix() ? 'scheduled for' : 'sent'
  } ${moment.unix(resendTime).format('MM/DD/YYYY hh:mm A')}`;

// Whether the message's resend to non-openers has gone out: its send time has
// passed and the message is not cancelled.
export const isResent = (message, now = moment().unix()) =>
  message.status !== 'cancelled' &&
  !!message.smartResendScheduleTime &&
  message.smartResendScheduleTime <= now;

// Resend markers for the combined graph: [{ time, label }] for each message
// whose resend has gone out.
export const getCombinedResends = messages =>
  messages
    .filter(message => isResent(message))
    .map(message => ({
      time: message.smartResendScheduleTime,
      label: `${message.draftName}: resend to non-openers sent ${moment
        .unix(message.smartResendScheduleTime)
        .format('MM/DD/YYYY hh:mm A')}`,
    }));
